function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { SPECIFICITY_TOKEN } from './constants';
import { queryServerStyleTag, isServer } from '../util';
const specificityRegExp = new RegExp(SPECIFICITY_TOKEN, 'g');
export class StyleSheet {
    /** Clean up after this stylesheet by un-applying it. */ flush() {
        this.serverRulesets.length = 0;
    }
    /** Returns true if the rule was successfully inserted */ insertRule(styles, index = -1) {
        const processedStyles = styles.replace(specificityRegExp, this.specificity);
        if (isServer) {
            if (index === -1) {
                this.serverRulesets.push(processedStyles);
            } else {
                this.serverRulesets.splice(index, 0, processedStyles);
            }
            return true;
        }
        const sheet = this.sheet;
        if (sheet) {
            try {
                sheet.insertRule(processedStyles, index === -1 ? sheet.cssRules.length : index);
            } catch (e) {
                // CSSStyleSheet.insertRule has several syntax restrictions and will
                // throw errors when it encounters certain types of unknown syntax
                // (in particular, unsupported pseudoselectors).
                //
                // We catch these errors for several reasons:
                // * We want to encourage progressive enhancement and leverage new
                //   browser features when possible.
                // * It’s difficult to know which pseudoselectors are supported in
                //   the current browser environment.
                // * Attempting to replace unknown syntax in each rule would penalize
                //   the many valid rules at the expense of the few invalid rules.
                //
                // We may want to consider logging here when in development, but it
                // could be noisy without some form of deduplication.
                //
                // TODO(koop): If a ruleset contains multiple selectors, can we try
                // inserting one ruleset per selector to preserve any working portions
                // of the ruleset?
                //
                // https://developer.mozilla.org/en-US/docs/Web/API/CSSStyleSheet/insertRule#restrictions
                if (process.env.NODE_ENV === 'development') {
                    // eslint-disable-next-line no-console
                    console.warn(e);
                }
                return false;
            }
        }
        return true;
    }
    constructor(getPreviousSheet, name){
        _define_property(this, "getPreviousSheet", void 0);
        _define_property(this, "name", void 0);
        _define_property(this, "serverRulesets", void 0);
        _define_property(this, "specificity", void 0);
        this.getPreviousSheet = getPreviousSheet;
        this.specificity = '';
        this.name = name || '';
        this.serverRulesets = [];
    }
}
export function InjectableConstructableStyleSheet(cssStyleSheetConstructor = ()=>new CSSStyleSheet()) {
    /**
   * A StyleSheet that is represented by an in-memory CSSStyleSheet instance.
   *
   * Requires the CSSStyleSheet constructor to be present (not uniformly true across
   * major browsers yet).
   */ return class ConstructableStyleSheet extends StyleSheet {
        flush() {
            if (this.sheet) {
                this.sheet.disabled = true;
            }
            this.sheet = undefined;
            super.flush();
        }
        constructor(getPreviousSheet, name){
            super(getPreviousSheet, name);
            _define_property(this, "sheet", void 0);
            _define_property(this, "type", 'constructable-sheet');
            this.sheet = cssStyleSheetConstructor(name);
        }
    };
}
function isElementStyleSheet(sheet) {
    return (sheet === null || sheet === void 0 ? void 0 : sheet.type) === 'element';
}
/** A StyleSheet that is mounted to a <style> element */ export class ElementStyleSheet extends StyleSheet {
    insertRule(styles, index = -1) {
        if (!this.element) {
            this.initializeElement();
        }
        return super.insertRule(styles, index);
    }
    initializeElement() {
        var _this_getPreviousSheet, _this;
        if (isServer || this.element) {
            return;
        }
        let previousElement;
        const previousSheet = (_this_getPreviousSheet = (_this = this).getPreviousSheet) === null || _this_getPreviousSheet === void 0 ? void 0 : _this_getPreviousSheet.call(_this);
        if (previousSheet && isElementStyleSheet(previousSheet)) {
            previousSheet.initializeElement();
            previousElement = previousSheet.element;
        }
        // Attempt to hydrate the element if one already exists
        this.element = queryServerStyleTag(this.name);
        if (!this.element) {
            // we're not hydrating, and/or this sheet doesn't have an associated tag
            this.element = document.createElement('style');
            // WebKit does not permit allowlisting empty style elements within Content
            // Security Policy directives unless `unsafe-inline` is specified. Appending
            // a non-empty string allows the element to be allowlisted via a content hash.
            // https://bugs.webkit.org/show_bug.cgi?id=233071
            this.element.appendChild(document.createTextNode('/**/'));
            this.element.setAttribute('data-layer', this.name);
            // Insert the <style> node in the correct position.
            if (previousElement) {
                previousElement.after(this.element);
            } else {
                document.head.appendChild(this.element);
            }
        }
    }
    flush() {
        if (this.element) {
            this.element.remove();
            this.element = undefined;
        }
        super.flush();
    }
    get sheet() {
        var _this_element;
        return ((_this_element = this.element) === null || _this_element === void 0 ? void 0 : _this_element.sheet) || undefined;
    }
    constructor(getPreviousSheet, name){
        super(getPreviousSheet, name);
        _define_property(this, "getPreviousSheet", void 0);
        _define_property(this, "element", void 0);
        _define_property(this, "type", void 0);
        this.getPreviousSheet = getPreviousSheet;
        this.element = undefined;
        this.type = 'element';
    }
}

//# sourceMappingURL=StyleSheet.js.map